import { PageHeader } from '@/components/primitives/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useRealtimeChannel } from '@/hooks/useRealtimeChannel'
import { useResourceList } from '@/hooks/useResourceList'
import {
  type BotDirective,
  type BotEngineConfig,
  burstBotDirective,
  controlBots,
  createBotDirective,
  endBotDirective,
  fetchBotEngine,
  fetchBotsStatus,
  listBotDirectives,
  patchBotEngine
} from '@/lib/p5'
import { notifyError } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Bot,
  ChevronUp,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  X,
  Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

const TIER_LABELS = [
  { key: 'tier1' as const, name: 'Gemini 2.5 Flash', color: 'bg-blue-500' },
  { key: 'tier2' as const, name: 'Claude Haiku 4.5', color: 'bg-violet-500' },
  {
    key: 'tier3' as const,
    name: 'Llama 3.3 70B (free)',
    color: 'bg-emerald-500'
  },
  { key: 'tier4' as const, name: 'DeepSeek v3.1 (free)', color: 'bg-amber-500' }
]

const ACTIVITY_OPTS = [
  { value: 'chill', label: 'Chill', hint: 'Low volume, natural gaps' },
  { value: 'normal', label: 'Normal', hint: 'Balanced daily rhythm' },
  {
    value: 'aggressive',
    label: 'Aggressive',
    hint: 'Max throughput, 3× volume'
  }
] as const

export function BotArmyPage() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [tiers, setTiers] = useState<BotEngineConfig['llm_tiers'] | null>(null)

  // ── Data ────────────────────────────────────────────────────────────────
  const status = useQuery({
    queryKey: ['bots-status'],
    queryFn: fetchBotsStatus,
    refetchInterval: 2_000
  })

  const engine = useQuery({
    queryKey: ['bots-engine'],
    queryFn: fetchBotEngine,
    refetchInterval: 2_000
  })

  // Seed local tier state from server once (only if user hasn't started editing)
  useEffect(() => {
    if (engine.data && !tiers) setTiers(engine.data.llm_tiers)
  }, [engine.data, tiers])

  const directives = useQuery({
    queryKey: ['bots-directives'],
    queryFn: () => listBotDirectives('active'),
    refetchInterval: 5_000
  })

  const actions = useResourceList('bots__botactionlog', {
    pageSize: 30,
    channel: 'admin.bots.actions'
  })

  useRealtimeChannel('admin.bots.actions', () => {
    qc.invalidateQueries({ queryKey: ['bots-status'] })
    qc.invalidateQueries({ queryKey: ['bots-engine'] })
  })

  // ── Mutations ────────────────────────────────────────────────────────────
  const controlMut = useMutation({
    mutationFn: (op: 'start' | 'stop' | 'cycle') => controlBots(op),
    onSuccess: (_d, op) => {
      toast.success(
        op === 'cycle'
          ? 'Cycle queued'
          : `Bot army ${op === 'start' ? 'started' : 'stopped'}`
      )
      qc.invalidateQueries({ queryKey: ['bots-status'] })
    },
    onError: (e) => notifyError('Failed', e)
  })

  const engineMut = useMutation({
    mutationFn: patchBotEngine,
    onSuccess: () => {
      toast.success('Engine config saved')
      qc.invalidateQueries({ queryKey: ['bots-engine'] })
    },
    onError: (e) => notifyError('Save failed', e)
  })

  const endMut = useMutation({
    mutationFn: endBotDirective,
    onSuccess: () => {
      toast.success('Directive ended')
      qc.invalidateQueries({ queryKey: ['bots-directives'] })
    },
    onError: (e) => notifyError('Failed', e)
  })

  const burstMut = useMutation({
    mutationFn: burstBotDirective,
    onSuccess: () => toast.success('Burst queued'),
    onError: (e) => notifyError('Failed', e)
  })

  // ── Derived ──────────────────────────────────────────────────────────────
  const isRunning = status.data?.is_running
  const todayCounts = engine.data?.today
  const activityLevel = engine.data?.activity_level ?? 'normal'
  const liveTiers = tiers ??
    engine.data?.llm_tiers ?? { tier1: 45, tier2: 15, tier3: 25, tier4: 15 }
  const tierTotal = Object.values(liveTiers).reduce((a, b) => a + b, 0)
  const tiersValid = tierTotal >= 95 && tierTotal <= 105

  function setTier(key: keyof typeof liveTiers, val: number) {
    setTiers((prev) => ({ ...(prev ?? liveTiers), [key]: val }))
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bot Army"
        description="Real-time control center for the promoter bot system."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={() => controlMut.mutate('cycle')}
            disabled={controlMut.isPending}
          >
            <RefreshCw className="size-3.5" /> Run cycle now
          </Button>
        }
      />

      {/* ── Status + today stats ─────────────────────────────────────────── */}
      <Card className="rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-4">
          <div
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-full',
              isRunning
                ? 'bg-emerald-500/10 text-emerald-600'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <Bot className="size-5" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">
                Bot army is {isRunning == null ? '—' : isRunning ? 'ON' : 'OFF'}
              </span>
              <Badge
                variant={isRunning ? 'default' : 'outline'}
                className="text-xs"
              >
                {isRunning ? 'Active' : 'Paused'}
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {status.data?.active ?? 0} bots active ·{' '}
              {status.data?.actions_last_hour ?? 0} actions last hour
            </p>
          </div>

          {/* Today quick stats */}
          <div className="flex gap-4 text-center">
            {[
              { label: 'posts', val: todayCounts?.post },
              { label: 'comments', val: todayCounts?.comment },
              { label: 'likes', val: todayCounts?.like },
              { label: 'reposts', val: todayCounts?.repost }
            ].map(({ label, val }) => (
              <div key={label}>
                <div className="text-lg font-semibold tabular-nums">
                  {val ?? '—'}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {label}
                </div>
              </div>
            ))}
          </div>

          {isRunning ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => controlMut.mutate('stop')}
              disabled={controlMut.isPending}
            >
              <Pause className="size-3.5" /> Stop
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => controlMut.mutate('start')}
              disabled={controlMut.isPending}
            >
              <Play className="size-3.5" /> Start
            </Button>
          )}
        </div>
      </Card>

      {/* ── Engine settings ──────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Activity level */}
        <Card className="rounded-2xl p-5">
          <h3 className="mb-3 text-sm font-semibold">Activity level</h3>
          <div className="grid grid-cols-3 gap-2">
            {ACTIVITY_OPTS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => engineMut.mutate({ activity_level: opt.value })}
                disabled={engineMut.isPending}
                className={cn(
                  'rounded-xl border p-3 text-left transition-colors',
                  activityLevel === opt.value
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-muted-foreground/40'
                )}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {opt.hint}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* LLM tier sliders */}
        <Card className="rounded-2xl p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">LLM tier mix</h3>
            <span
              className={cn(
                'text-xs tabular-nums',
                !tiersValid && 'text-destructive'
              )}
            >
              {tierTotal}% {!tiersValid && '(must equal 100)'}
            </span>
          </div>
          <div className="space-y-3">
            {TIER_LABELS.map(({ key, name, color }) => (
              <div key={key}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{name}</span>
                  <span className="font-medium tabular-nums">
                    {liveTiers[key]}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 h-1.5 rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', color)}
                      style={{ width: `${Math.min(liveTiers[key], 100)}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={liveTiers[key]}
                    onChange={(e) => setTier(key, Number(e.target.value))}
                    className="w-20 accent-primary"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button
            size="sm"
            className="mt-4 w-full"
            disabled={!tiersValid || engineMut.isPending}
            onClick={() => engineMut.mutate({ llm_tiers: liveTiers })}
          >
            {engineMut.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : null}
            Save tier mix
          </Button>
        </Card>
      </div>

      {/* ── Active directives ────────────────────────────────────────────── */}
      <Card className="rounded-2xl">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Active directives</h3>
          <Badge variant="secondary" className="ml-1">
            {directives.data?.results.length ?? 0}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto gap-1"
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? (
              <>
                <ChevronUp className="size-3.5" /> Cancel
              </>
            ) : (
              <>
                <Plus className="size-3.5" /> New
              </>
            )}
          </Button>
        </div>

        <AnimatePresence>
          {showCreate && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-b"
            >
              <CreateDirectiveForm
                onCreated={() => {
                  setShowCreate(false)
                  qc.invalidateQueries({ queryKey: ['bots-directives'] })
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="divide-y">
          {directives.isLoading ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : (directives.data?.results ?? []).length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No active directives — bots are running on organic noise.
            </div>
          ) : (
            (directives.data?.results ?? []).map((d) => (
              <DirectiveRow
                key={d.id}
                d={d}
                onEnd={() => endMut.mutate(d.id)}
                onBurst={() => burstMut.mutate(d.id)}
                ending={endMut.isPending}
                bursting={burstMut.isPending}
              />
            ))
          )}
        </div>
      </Card>

      {/* ── Live action feed ─────────────────────────────────────────────── */}
      <Card className="rounded-2xl">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <span className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
          <h3 className="text-sm font-semibold">Live feed</h3>
          {actions.isFetching && (
            <span className="text-xs text-muted-foreground">·</span>
          )}
        </div>
        <div className="max-h-80 overflow-auto">
          {(actions.data?.results ?? []).length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No activity yet.
            </div>
          ) : (
            (actions.data?.results ?? []).map((row, idx) => (
              <motion.div
                key={String(row.id)}
                initial={idx < 4 ? { opacity: 0, y: -2 } : false}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 border-b px-4 py-2 text-sm last:border-0"
              >
                <ActionBadge
                  type={String(row.action ?? row.action_type ?? '')}
                />
                <span className="truncate text-muted-foreground">
                  {String(row.action ?? row.action_type ?? 'action')} #
                  {String(row.target_id ?? row.id)}
                </span>
                <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground">
                  {row.created_at
                    ? new Date(String(row.created_at)).toLocaleTimeString()
                    : ''}
                </span>
              </motion.div>
            ))
          )}
        </div>
      </Card>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ActionBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    post: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
    comment: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
    like: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
    repost: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    reply: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
  }
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        colors[type] ?? 'bg-muted text-muted-foreground'
      )}
    >
      {type || 'action'}
    </span>
  )
}

function DirectiveRow({
  d,
  onEnd,
  onBurst,
  ending,
  bursting
}: {
  d: BotDirective
  onEnd: () => void
  onBurst: () => void
  ending: boolean
  bursting: boolean
}) {
  const postPct =
    d.posts_target > 0
      ? Math.min((d.posts_done / d.posts_target) * 100, 100)
      : 0
  const commentPct =
    d.comments_target > 0
      ? Math.min((d.comments_done / d.comments_target) * 100, 100)
      : 0

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{d.title}</span>
            <Badge variant="outline" className="shrink-0 text-[10px]">
              {d.priority}
            </Badge>
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {d.tone}
            </Badge>
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {d.direction}
          </p>
          <div className="mt-2 flex gap-4">
            <ProgressBar
              label="posts"
              done={d.posts_done}
              target={d.posts_target}
              pct={postPct}
            />
            <ProgressBar
              label="comments"
              done={d.comments_done}
              target={d.comments_target}
              pct={commentPct}
            />
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={onBurst}
            disabled={bursting}
            className="gap-1 text-xs"
          >
            <Zap className="size-3" /> Burst
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onEnd}
            disabled={ending}
            className="text-destructive hover:text-destructive"
          >
            <X className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function ProgressBar({
  label,
  done,
  target,
  pct
}: { label: string; done: number; target: number; pct: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="w-16 h-1 rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[11px] tabular-nums text-muted-foreground">
        {done}/{target}
      </span>
    </div>
  )
}

function CreateDirectiveForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    title: '',
    direction: '',
    tone: 'chill',
    priority: 'normal',
    posts_target: 10,
    comments_target: 20,
    hours: 6
  })

  const mut = useMutation({
    mutationFn: () => createBotDirective(form),
    onSuccess: () => {
      toast.success('Directive created')
      onCreated()
    },
    onError: (e) => notifyError('Failed to create', e)
  })

  const update = (k: keyof typeof form, v: string | number) =>
    setForm((prev) => ({ ...prev, [k]: v }))

  return (
    <div className="grid gap-4 p-4">
      <div className="grid gap-1.5">
        <Label>Title</Label>
        <Input
          placeholder="e.g. HBCU Week celebration"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          maxLength={60}
        />
      </div>
      <div className="grid gap-1.5">
        <Label>
          Direction{' '}
          <span className="text-muted-foreground text-xs">
            (what the AI should post about)
          </span>
        </Label>
        <Textarea
          placeholder='e.g. "Celebrate HBCU culture this week. Share pride, memories, wins. Pick ONE angle: your school, a moment, what HBCUs mean to you."'
          rows={4}
          value={form.direction}
          onChange={(e) => update('direction', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="grid gap-1.5">
          <Label>Tone</Label>
          <Select value={form.tone} onValueChange={(v) => update('tone', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="chill">Chill</SelectItem>
              <SelectItem value="excited">Excited</SelectItem>
              <SelectItem value="provocative">Provocative</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>Priority</Label>
          <Select
            value={form.priority}
            onValueChange={(v) => update('priority', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>Posts target</Label>
          <Input
            type="number"
            min={1}
            max={500}
            value={form.posts_target}
            onChange={(e) => update('posts_target', Number(e.target.value))}
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Comments target</Label>
          <Input
            type="number"
            min={0}
            max={1000}
            value={form.comments_target}
            onChange={(e) => update('comments_target', Number(e.target.value))}
          />
        </div>
      </div>
      <div className="grid gap-1.5 max-w-xs">
        <Label>Duration (hours)</Label>
        <Input
          type="number"
          min={1}
          max={168}
          value={form.hours}
          onChange={(e) => update('hours', Number(e.target.value))}
        />
      </div>
      <Button
        disabled={!form.title.trim() || !form.direction.trim() || mut.isPending}
        onClick={() => mut.mutate()}
        className="w-fit"
      >
        {mut.isPending && <Loader2 className="size-3.5 animate-spin" />}
        Create directive
      </Button>
    </div>
  )
}
